import { useState, useRef, useEffect, useMemo, useCallback, Fragment, type JSX } from 'react'
import { chatDisplayName, useWhatsAppChatMessages } from '../../hooks/useWhatsAppChats.js'
import { useUploadAttachment } from '../../hooks/useUploadAttachment.js'
import { WhatsAppIcon } from '../conversations/BrandIcons.js'
import { TicketGroupBlock } from '../chat-shared/TicketGroupBlock.js'
import { groupMessagesByTicket } from '../chat-shared/ticketGrouping.js'
import { dayBoundaries } from '../chat-shared/dayGrouping.js'
import { placeHandoffEvents } from '../chat-shared/timeline.js'
import { HandoffDivider } from '../chat-shared/HandoffDivider.js'
import { useStickyScroll } from '../chat-shared/useStickyScroll.js'
import { useChannelTyping } from '../chat-shared/useChannelTyping.js'
import { useTypingSignal } from '../chat-shared/useTypingSignal.js'
import { TypingIndicator } from '../chat-shared/TypingIndicator.js'
import { TemplatePickerDialog } from './TemplatePickerDialog.js'
import { SaveContactDialog } from './SaveContactDialog.js'
import { WhatsAppMessageBubble, PendingBubble, WhatsAppCallEntry } from './WhatsAppMessageBubble.js'
import {
  Send,
  Ticket,
  X,
  Phone,
  UserPlus,
  FileText,
  Clock,
  Paperclip,
  Loader2,
  ChevronDown,
  ArrowRightLeft,
  Bot,
} from 'lucide-react'
import { formatDateTime } from '../../utils/formatDate.js'
import { timestampMs } from '../../utils/timezone.js'
import { ToolButton } from '../../components/conversations/detail/primitives.js'
import { api } from '../../api/client.js'

interface Props {
  chatId: string | null
  onCall: (input: { chatId: string; label: string }) => void
  callDisabled?: boolean
  onChanged?: () => void
  isAssignedToMe?: boolean
  isAiAssigned?: boolean
  aiAgentName?: string | null
  onOpenTransfer?: () => void
  onOpenDetails?: () => void
}

/** WhatsApp caps media at 16 MB regardless of what the upload route allows. */
const MAX_ATTACHMENT_BYTES = 16 * 1024 * 1024

/** Centered date chip marking where a new day starts in the thread. */
function DaySeparator({ label }: { label: string }): JSX.Element {
  return (
    <div className="my-2 flex justify-center">
      <span className="rounded-full bg-white/90 px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-gray-500 shadow-sm">
        {label}
      </span>
    </div>
  )
}

export function WhatsAppChatThread({ chatId, onCall, callDisabled, onChanged, isAssignedToMe, isAiAssigned, aiAgentName, onOpenTransfer, onOpenDetails }: Props): JSX.Element {
  const {
    chat,
    messages,
    handoffEvents,
    calls,
    conversations,
    pending,
    loading,
    loadingMore,
    hasMore,
    sessionOpen,
    sessionExpiresAt,
    sendMessage,
    sendTemplate,
    sendAttachment,
    saveContact,
    createTicket,
    dismissPending,
    loadMore,
  } = useWhatsAppChatMessages(chatId)

  const [input, setInput] = useState('')
  const { actors: typingActors } = useChannelTyping('whatsapp', chatId)
  const { signalTyping, stopTyping } = useTypingSignal('whatsapp', chatId)
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<string | null>(null)
  const [creatingTicket, setCreatingTicket] = useState(false)
  const [ticketResult, setTicketResult] = useState<{ referenceId: string } | null>(null)
  const [showTemplates, setShowTemplates] = useState(false)
  const [showSaveContact, setShowSaveContact] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const fetchingMoreRef = useRef(false)
  // Only follows new messages while the agent is already at the bottom.
  const { containerRef, atBottom, unseen, scrollToBottom, handleScroll: stickyHandleScroll } = useStickyScroll(
    `${chatId}:${messages.length}:${pending.length}`,
  )
  const { upload } = useUploadAttachment()

  const onScroll = useCallback(() => {
    stickyHandleScroll()
    if (fetchingMoreRef.current || !hasMore || loadingMore) return
    const el = containerRef.current
    if (!el) return
    if (el.scrollTop <= el.clientHeight * 0.2) {
      const oldScrollHeight = el.scrollHeight
      const oldScrollTop = el.scrollTop
      fetchingMoreRef.current = true
      void loadMore().finally(() => {
        requestAnimationFrame(() => {
          const next = containerRef.current
          if (!next) return
          const added = next.scrollHeight - oldScrollHeight
          if (added > 0) {
            next.scrollTop = oldScrollTop + added
          }
          fetchingMoreRef.current = false
        })
      })
    }
  }, [hasMore, loadingMore, loadMore, stickyHandleScroll, containerRef])

  const groups = useMemo(() => groupMessagesByTicket(messages), [messages])
  const handoffPlacement = useMemo(
    () => placeHandoffEvents(messages, handoffEvents),
    [messages, handoffEvents],
  )
  const conversationById = useMemo(
    () => new Map(conversations.map((c) => [c.id, c])),
    [conversations],
  )
  const unlinkedCount = useMemo(
    () => messages.filter((m) => !m.conversation_id).length,
    [messages],
  )
  // Boundaries are computed over the flattened render order, so a day chip
  // appears once per calendar day rather than once per conversation group.
  const dayStarts = useMemo(
    () => dayBoundaries(groups.flatMap((g) => g.messages)),
    [groups],
  )

  /**
   * Calls interleaved with the message groups by time. A call belongs in the
   * thread where it happened — showing them only in a separate list loses the
   * "they called right after this message" context an agent is reading for.
   */
  const timeline = useMemo(() => {
    const items: Array<
      | { kind: 'group'; at: number; key: string; group: (typeof groups)[number] }
      | { kind: 'call'; at: number; key: string; call: (typeof calls)[number] }
    > = []
    for (const group of groups) {
      const last = group.messages[group.messages.length - 1]
      items.push({
        kind: 'group',
        at: last ? timestampMs(last.sent_at) : 0,
        key: group.key,
        group,
      })
    }
    for (const call of calls) {
      items.push({ kind: 'call', at: timestampMs(call.started_at), key: `call-${call.id}`, call })
    }
    return items.sort((a, b) => a.at - b.at)
  }, [groups, calls])

  useEffect(() => {
    setTicketResult(null)
    setSendError(null)
    setInput('')
    setFile(null)
  }, [chatId])

  async function handleCreateTicket(): Promise<void> {
    if (!isAssignedToMe || creatingTicket || unlinkedCount === 0) return
    setCreatingTicket(true)
    setSendError(null)
    try {
      // No message ids — the API promotes everything not already on a conversation.
      const result = await createTicket()
      if (result) {
        setTicketResult({ referenceId: result.referenceId })
        onChanged?.()
      }
    } catch {
      setSendError('Could not create the conversation.')
    } finally {
      setCreatingTicket(false)
    }
  }

  function pickFile(): void {
    fileInputRef.current?.click()
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const picked = e.target.files?.[0]
    if (!picked) return
    if (picked.size > MAX_ATTACHMENT_BYTES) {
      setSendError('WhatsApp attachments are limited to 16 MB.')
      return
    }
    setSendError(null)
    setFile(picked)
  }

  function clearFile(): void {
    setFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  async function handleSend(): Promise<void> {
    if (!isAssignedToMe || !chat || chat.status !== 'active') return
    if (sending || uploading) return
    if (!file && !input.trim()) return
    stopTyping()
    setSendError(null)

    // With a file attached the caption rides along with it, so this is one send.
    if (file) {
      setUploading(true)
      try {
        const uploaded = await upload(file)
        setUploading(false)
        setSending(true)
        await sendAttachment({
          attachmentId: uploaded.attachmentId,
          filename: uploaded.filename,
          ...(input.trim() ? { caption: input.trim() } : {}),
        })
        setInput('')
        clearFile()
        onChanged?.()
      } catch (err) {
        const status = (err as { status?: number }).status
        setSendError(
          status === 409
            ? 'The 24-hour window has closed — attachments need an open window.'
            : 'Attachment could not be sent.',
        )
      } finally {
        setUploading(false)
        setSending(false)
      }
      return
    }

    setSending(true)
    try {
      await sendMessage(input)
      setInput('')
      onChanged?.()
    } catch (err) {
      const status = (err as { status?: number }).status
      setSendError(
        status === 409
          ? 'The 24-hour window has closed — send an approved template instead.'
          : 'Message could not be sent.',
      )
    } finally {
      setSending(false)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleSend()
    }
  }

  if (!chatId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center bg-[#F0F2F5]">
        <WhatsAppIcon className="mb-3 h-12 w-12 text-[#25D366]/30" />
        <p className="text-sm text-gray-400">Select a chat to start messaging on WhatsApp</p>
      </div>
    )
  }

  const displayName = chat ? chatDisplayName(chat) : 'WhatsApp'
  const windowLabel = sessionExpiresAt
    ? `Reply window ${sessionOpen ? 'closes' : 'closed'} ${formatDateTime(sessionExpiresAt)}`
    : 'No inbound message yet — templates only'
  const busy = sending || uploading

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[#EFEAE2]">
      {/* Header */}
      <div className="shrink-0 flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#25D366]/10">
          <WhatsAppIcon className="h-5 w-5 text-[#25D366]" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-gray-800">
            <button type="button" onClick={onOpenDetails} className="hover:text-blue-600">
              {displayName}
            </button>
          </h3>
          <p className="truncate text-[11px] text-gray-400">
            +{chat?.wa_id}
            {chat?.client_id ? ' · saved contact' : ''}
          </p>
        </div>

        <div className="flex items-center gap-1.5">
          <button
            onClick={() => chat && onCall({ chatId: chat.id, label: displayName })}
            disabled={callDisabled || !chat}
            title="Call on WhatsApp"
            className="flex h-8 w-8 items-center justify-center rounded-full text-[#128C7E] transition-colors hover:bg-[#25D366]/10 disabled:opacity-40"
          >
            <Phone className="h-4 w-4" />
          </button>
          <button
            onClick={() => setShowSaveContact(true)}
            title={chat?.client_id ? 'Update contact' : 'Add to contacts'}
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <UserPlus className="h-4 w-4" />
          </button>
          <button
            onClick={() => void handleCreateTicket()}
            disabled={!isAssignedToMe || unlinkedCount === 0 || creatingTicket}
            title={
              unlinkedCount === 0
                ? 'Every message is already on a conversation'
                : `Create a conversation from ${unlinkedCount} unlinked message${unlinkedCount === 1 ? '' : 's'}`
            }
            className="flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-40"
          >
            <Ticket className="h-3.5 w-3.5" />
            {creatingTicket ? 'Creating…' : 'Create Conversation'}
            {unlinkedCount > 0 && !creatingTicket && (
              <span className="rounded-full bg-[#25D366]/15 px-1.5 text-[10px] font-semibold text-[#128C7E]">
                {unlinkedCount}
              </span>
            )}
          </button>
          {(isAssignedToMe || isAiAssigned) && onOpenTransfer && (
            <ToolButton label="Transfer" icon={<ArrowRightLeft className="h-4 w-4" />} onClick={onOpenTransfer} />
          )}
        </div>
      </div>

      {isAiAssigned && (
        <div className="flex items-center justify-between border-b border-purple-200 bg-purple-50 px-4 py-2">
          <span className="flex items-center gap-1.5 text-xs font-medium text-purple-700">
            <Bot className="h-3.5 w-3.5" />
            {aiAgentName ?? 'AI Agent'} is handling this chat
          </span>
          {!isAssignedToMe && (
            <button
              type="button"
              onClick={() => void api.post(`/agent/whatsapp-chats/${chatId}/assign`, {}).then(() => { onChanged?.() }).catch(() => {})}
              className="flex items-center gap-1.5 rounded-lg border border-purple-300 bg-white px-3 py-1 text-xs font-medium text-purple-700 transition-all hover:bg-purple-50 active:scale-[0.97]"
            >
              <UserPlus className="h-3 w-3" />
              Assign to Me
            </button>
          )}
        </div>
      )}

      {ticketResult && (
        <div className="flex items-center justify-between border-b border-green-200 bg-green-50 px-4 py-2">
          <span className="text-xs text-green-700">
            Conversation created: <strong>{ticketResult.referenceId}</strong>
          </span>
          <button onClick={() => setTicketResult(null)} className="text-gray-400 hover:text-gray-600">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Messages, grouped by the conversation they belong to */}
      <div className="relative flex-1 overflow-hidden">
      <div ref={containerRef} onScroll={onScroll} className="h-full overflow-y-auto thin-scrollbar px-4 py-4">
        {loadingMore && (
          <div className="flex items-center justify-center gap-2 py-2 text-xs text-gray-500">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
            Loading older messages…
          </div>
        )}
        {loading && messages.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-sm text-gray-500">Loading messages…</div>
        ) : messages.length === 0 && pending.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-sm text-gray-500">
            No messages yet — say hello.
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            {timeline.map((item) =>
              item.kind === 'call' ? (
                <WhatsAppCallEntry key={item.key} call={item.call} />
              ) : item.group.type === 'ticket' ? (
                <TicketGroupBlock
                  key={item.key}
                  conversationId={item.group.conversationId}
                  conversation={conversationById.get(item.group.conversationId)}
                  messageCount={item.group.messages.length}
                >
                  {item.group.messages.map((msg) => (
                    <Fragment key={msg.id}>
                      {dayStarts.has(msg.id) && <DaySeparator label={dayStarts.get(msg.id)!} />}
                      {handoffPlacement.before.get(msg.id)?.map((e) => <HandoffDivider key={e.id} event={e} />)}
                      <WhatsAppMessageBubble chatId={chatId} msg={msg} />
                    </Fragment>
                  ))}
                </TicketGroupBlock>
              ) : (
                <Fragment key={item.key}>
                  {item.group.messages.map((msg) => (
                    <Fragment key={msg.id}>
                      {dayStarts.has(msg.id) && <DaySeparator label={dayStarts.get(msg.id)!} />}
                      {handoffPlacement.before.get(msg.id)?.map((e) => <HandoffDivider key={e.id} event={e} />)}
                      <WhatsAppMessageBubble chatId={chatId} msg={msg} />
                    </Fragment>
                  ))}
                </Fragment>
              ),
            )}
            {handoffPlacement.trailing.map((e) => <HandoffDivider key={e.id} event={e} />)}
            {pending.map((p) => (
              <PendingBubble key={p.localId} msg={p} onDismiss={dismissPending} />
            ))}
          </div>
        )}
      </div>

      {/* Jump back to the newest message; badge counts what arrived meanwhile. */}
      {!atBottom && (
        <button
          onClick={() => scrollToBottom()}
          className="absolute bottom-4 right-4 flex items-center gap-1.5 rounded-full bg-white px-3 py-2 text-xs font-medium text-gray-600 shadow-lg ring-1 ring-gray-200 transition-colors hover:bg-gray-50"
          title="Jump to latest"
        >
          <ChevronDown className="h-4 w-4" />
          {unseen > 0 && (
            <span className="rounded-full bg-[#25D366] px-1.5 py-0.5 text-[10px] font-bold text-white">
              {unseen}
            </span>
          )}
        </button>
      )}
      </div>

      {/* Session window notice */}
      <div
        className={`shrink-0 flex items-center gap-2 border-t px-4 py-1.5 text-[11px] ${
          sessionOpen
            ? 'border-gray-200 bg-white text-gray-400'
            : 'border-amber-200 bg-amber-50 text-amber-700'
        }`}
      >
        <Clock className="h-3 w-3 flex-shrink-0" />
        <span className="truncate">{windowLabel}</span>
        {!sessionOpen && (
          <button
            onClick={() => setShowTemplates(true)}
            className="ml-auto flex-shrink-0 font-semibold underline underline-offset-2 hover:text-amber-800"
          >
            Send a template
          </button>
        )}
      </div>

      <TypingIndicator actors={typingActors} />

      {sendError && (
        <div className="border-t border-red-200 bg-red-50 px-4 py-1.5 text-[11px] text-red-600">{sendError}</div>
      )}

      {file && (
        <div className="flex items-center gap-2 border-t border-gray-200 bg-white px-4 py-2">
          <Paperclip className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
          <span className="truncate text-xs text-gray-600">{file.name}</span>
          <span className="flex-shrink-0 text-[10px] text-gray-400">
            {(file.size / 1024).toFixed(0)} KB
          </span>
          <button onClick={clearFile} className="ml-auto text-gray-400 hover:text-gray-600">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {isAssignedToMe && (
        <div className="shrink-0 border-t border-gray-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileChange}
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar"
          />
          <button
            onClick={pickFile}
            disabled={!isAssignedToMe || !chat || chat.status !== 'active' || !sessionOpen || busy}
            title={sessionOpen ? 'Attach a file' : 'Attachments need an open 24h window'}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-[#128C7E] disabled:opacity-40"
          >
            <Paperclip className="h-4 w-4" />
          </button>
          <button
            onClick={() => setShowTemplates(true)}
            disabled={!isAssignedToMe || !chat || chat.status !== 'active'}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-[#128C7E]"
          >
            <FileText className="h-4 w-4" />
          </button>
          <input
            type="text"
            value={input}
            onChange={(e) => { setInput(e.target.value); signalTyping() }}
            onKeyDown={handleKeyDown}
            placeholder={
              file
                ? 'Add a caption…'
                : sessionOpen
                  ? 'Type a message…'
                  : 'Outside the 24h window — use a template'
            }
            disabled={!isAssignedToMe || !chat || chat.status !== 'active' || busy || (!sessionOpen && !file)}
            className="flex-1 rounded-full border border-gray-200 px-4 py-2 text-sm outline-none transition-colors focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366]/20 disabled:bg-gray-50 disabled:opacity-60"
          />
          <button
            onClick={() => void handleSend()}
            disabled={!isAssignedToMe || !chat || chat.status !== 'active' || (!input.trim() && !file) || busy || !sessionOpen}
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#25D366] text-white transition-colors hover:bg-[#128C7E] disabled:opacity-40 disabled:hover:bg-[#25D366]"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </div>
      )}

      {showTemplates && (
        <TemplatePickerDialog
          onClose={() => setShowTemplates(false)}
          onSend={async (payload) => {
            await sendTemplate(payload)
            setShowTemplates(false)
            onChanged?.()
          }}
        />
      )}

      {showSaveContact && chat && (
        <SaveContactDialog
          waId={chat.wa_id}
          initialName={chat.display_name ?? chat.client_name ?? chat.profile_name ?? ''}
          initialEmail={chat.client_email ?? ''}
          onClose={() => setShowSaveContact(false)}
          onSave={async (payload) => {
            await saveContact(payload)
            setShowSaveContact(false)
            onChanged?.()
          }}
        />
      )}
    </div>
  )
}
